import type { ReactNode } from "react";
import { cn } from "./cn.js";
import { focusRing } from "./interactive.js";

export type Tab<T extends string = string> = {
  id: T;
  label: ReactNode;
  badge?: ReactNode;
};

type Props<T extends string> = {
  tabs: readonly Tab<T>[];
  value: T;
  onChange: (id: T) => void;
  className?: string;
};

/** Linear-style segmented tab control. Underlines the active tab; minimal
 *  contrast on inactive ones. */
export function Tabs<T extends string>({ tabs, value, onChange, className }: Props<T>) {
  return (
    <div
      role="tablist"
      className={cn(
        "inline-flex items-center gap-0.5 rounded-md border border-border bg-surface-muted/60 p-0.5",
        className,
      )}
    >
      {tabs.map((t) => {
        const active = t.id === value;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.id)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-[5px] px-3 h-7 text-[13px]",
              "transition-colors duration-150",
              focusRing,
              active
                ? "bg-surface-elevated text-ink shadow-[inset_0_0_0_1px_var(--p-border-strong)]"
                : "text-ink-muted hover:text-ink",
            )}
          >
            {t.label}
            {t.badge && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-surface-elevated text-[10px] font-mono text-ink-muted px-1">
                {t.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
